import React from 'react';
import Panel from '../../components/ui/Panel';
import ComponentShowcase from '../../components/ui/ComponentShowcase';
import Input from '../../components/ui/Input';

const textInputCode = `<Input label="Username" placeholder="Enter username" />`;
const numberInputCode = `<Input label="Quantity" type="number" min={1} max={1000} placeholder="Enter amount" />`;
const statesCode = `<Input label="Default" placeholder="Default input" />
<Input label="Disabled" placeholder="Disabled input" disabled />`;
const validationCode = `<Input label="Name" status="valid" message="Name accepted." defaultValue="Zezima" />
<Input label="Amount" status="invalid" message="Enter a number." defaultValue="x" />`;

const InputPage: React.FC = () => {
  return (
    <div>
      <h1 id="overview" className="text-3xl text-osrs-gold font-bold uppercase mb-4">Input Fields</h1>
      <p className="text-osrs-yellow mb-8">
        Amount entry and chat typing sit in an inset field. Text is the same yellow sampled from bank interface copy. The field is square, with the dark edge on the top and left.
      </p>

      <section id="text-input" className="mb-12">
        <h2 className="text-2xl text-osrs-gold font-bold uppercase mb-6">Text Input</h2>
        <ComponentShowcase
          title="Basic Text Input"
          description="A labelled text field. The label is part of the component."
          component={<div className="w-full max-w-md"><Input label="Username" placeholder="Enter username" /></div>}
          code={textInputCode}
        />
      </section>

      <section id="number-input" className="mb-12">
        <h2 className="text-2xl text-osrs-gold font-bold uppercase mb-6">Number Input</h2>
        <ComponentShowcase
          title="Number Input"
          description="Quantity prompts use the same chrome with type number."
          component={<div className="w-full max-w-md"><Input label="Quantity" type="number" min={1} max={1000} placeholder="Enter amount" /></div>}
          code={numberInputCode}
        />
      </section>

      <section id="states" className="mb-12">
        <h2 className="text-2xl text-osrs-gold font-bold uppercase mb-6">States</h2>
        <ComponentShowcase
          title="Default and disabled"
          description="Disabled fields keep the inset and drop their contrast."
          component={
            <div className="w-full max-w-md space-y-4">
              <Input label="Default" placeholder="Default input" />
              <Input label="Disabled" placeholder="Disabled input" disabled />
            </div>
          }
          code={statesCode}
        />
      </section>

      <section id="validation" className="mb-12">
        <h2 className="text-2xl text-osrs-gold font-bold uppercase mb-6">Validation</h2>
        <ComponentShowcase
          title="Valid and invalid"
          description="Green marks an accepted value. Red marks a rejected one. The message is tied to the field."
          component={
            <div className="w-full max-w-md space-y-4">
              <Input label="Name" status="valid" message="Name accepted." defaultValue="Zezima" />
              <Input label="Amount" status="invalid" message="Enter a number." defaultValue="x" />
            </div>
          }
          code={validationCode}
        />
      </section>

      <section id="usage-guidelines" className="mb-12">
        <h2 className="text-2xl text-osrs-gold font-bold uppercase mb-6">Usage Guidelines</h2>
        <Panel>
          <h3 id="when-to-use" className="text-xl text-osrs-gold font-bold mb-3">When to Use</h3>
          <ul className="list-disc list-inside text-osrs-yellow space-y-2 ml-4">
            <li>Use for names, amounts, and search.</li>
            <li>Keep the label visible. Placeholder text is a hint, not the name of the field.</li>
            <li>Use the invalid status when the value cannot be submitted.</li>
          </ul>
        </Panel>
      </section>

      <section id="api-reference" className="mb-12">
        <h2 className="text-2xl text-osrs-gold font-bold uppercase mb-6">API Reference</h2>
        <Panel>
          <table className="w-full text-left text-osrs-yellow">
            <thead>
              <tr className="text-osrs-gold">
                <th className="py-2 px-4">Prop</th>
                <th className="py-2 px-4">Type</th>
                <th className="py-2 px-4">Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="py-2 px-4">label</td>
                <td className="py-2 px-4">string</td>
                <td className="py-2 px-4">Visible name of the field</td>
              </tr>
              <tr>
                <td className="py-2 px-4">status</td>
                <td className="py-2 px-4">default | valid | invalid</td>
                <td className="py-2 px-4">Border and message colour</td>
              </tr>
              <tr>
                <td className="py-2 px-4">message</td>
                <td className="py-2 px-4">string</td>
                <td className="py-2 px-4">Hint or error under the field</td>
              </tr>
            </tbody>
          </table>
        </Panel>
      </section>
    </div>
  );
};

export default InputPage;
